import React, { useId, useState } from 'react';
import Link from '../detail-pages/Link';
import { TemplatedLazyImage } from '../../components/image';
import placeholderSrc from '../restaurant-details/components/menu/placeholder.svg';
import { Button } from '@/ui/components/button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/ui/components/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/ui/components/dropdown-menu';
import {
    SignedOutError,
    errorText,
    v2Session,
} from '../../components/lists/lists';
import {
    ADD_COMMENT,
    ADD_REACTION,
    BLOCK_USER,
    DELETE_COMMENT,
    DELETE_POST,
    MAX_COMMENT,
    MAX_REPORT_DETAILS,
    POST_COMMENTS,
    REACTIONS,
    REMOVE_REACTION,
    REPORT_COMMENT,
    REPORT_POST,
    REPORT_REASONS,
    authorName,
    commentCountText,
    initials,
    timeAgo,
    toggleReaction,
    type FeedAuthor,
    sized,
    type FeedComment,
    type FeedImage,
    type FeedItem,
    type FeedPost,
    type FeedRestaurant,
    type ReportReason,
} from '../../components/feed/feed';
import {
    BlockIcon,
    ChevronIcon,
    DotsIcon,
    FlagIcon,
    SendIcon,
    SmileIcon,
    TrashIcon,
} from './icons';

/** One post (prototype MyFeed PostCard): author, words or the saved item, reactions, comments. */

const SESSION_ENDED = 'Your session has ended. Log in again to carry on.';
const failure = (err: unknown, fallback: string) =>
    err instanceof SignedOutError ? SESSION_ENDED : errorText(err, fallback);

export const profilePath = (username: string) =>
    process.env.ENABLE_PUBLIC_PROFILES
        ? `/user/${encodeURIComponent(username)}`
        : `/user/${encodeURIComponent(username)}/lists`;

export const Avatar = ({
    author,
    size = 'md',
}: {
    author: FeedAuthor;
    size?: 'sm' | 'md';
}) => (
    <span
        className={`flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-border-subtle bg-surface-subtle font-medium text-ink-muted ${size === 'sm' ? 'size-9 text-caption' : 'size-11 text-body-2'}`}
    >
        {author.avatarUrl ? (
            <img
                src={author.avatarUrl}
                alt=""
                referrerPolicy="no-referrer"
                className="size-full object-cover"
            />
        ) : (
            initials(authorName(author))
        )}
    </span>
);

const AuthorLink = ({ author }: { author: FeedAuthor }) => (
    <Link
        to={profilePath(author.username)}
        className="font-semibold text-ink-strong no-underline outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
    >
        {authorName(author)}
    </Link>
);

const roundButton =
    'inline-flex size-9 shrink-0 items-center justify-center rounded-full text-ink-muted outline-none transition-colors hover:bg-surface-subtle hover:text-ink focus-visible:ring-2 focus-visible:ring-ring';

/** Report (into the admin queue), as a bottom sheet on phones. */
const ReportDialog = ({
    target,
    onClose,
}: {
    target: { kind: 'post' | 'comment'; id: string } | null;
    onClose: () => void;
}) => {
    const uid = useId();
    const [reason, setReason] = useState<ReportReason | null>(null);
    const [details, setDetails] = useState('');
    const [state, setState] = useState<'idle' | 'busy' | 'sent'>('idle');
    const [error, setError] = useState('');

    const close = () => {
        onClose();
        setReason(null);
        setDetails('');
        setState('idle');
        setError('');
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!target || !reason) return;
        setState('busy');
        setError('');
        try {
            const vars = { reason, details: details.trim() || null };
            await (target.kind === 'post'
                ? v2Session(REPORT_POST, { postId: target.id, ...vars })
                : v2Session(REPORT_COMMENT, { commentId: target.id, ...vars }));
            setState('sent');
        } catch (err) {
            setState('idle');
            setError(failure(err, "We couldn't send your report. Try again."));
        }
    };

    const what = target?.kind === 'comment' ? 'comment' : 'post';
    return (
        <Dialog open={target !== null} onOpenChange={open => !open && close()}>
            <DialogContent className="max-md:top-auto max-md:max-h-[85vh] max-md:rounded-t-lg">
                <DialogHeader>
                    <DialogTitle className="text-subtitle-1 font-semibold">
                        Report {what}
                    </DialogTitle>
                </DialogHeader>
                {state === 'sent' ? (
                    <>
                        <DialogBody>
                            <DialogDescription className="text-body-1 text-ink">
                                Thanks for telling us. Our team will review this{' '}
                                {what}.
                            </DialogDescription>
                        </DialogBody>
                        <DialogFooter>
                            <Button size="sm" onClick={close}>
                                Done
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <form
                        onSubmit={submit}
                        className="flex min-h-0 flex-1 flex-col"
                    >
                        <DialogBody className="flex flex-col gap-3">
                            <DialogDescription className="text-body-2 text-ink-muted">
                                Why are you reporting this {what}?
                            </DialogDescription>
                            <fieldset className="flex flex-col gap-1">
                                <legend className="sr-only">Reason</legend>
                                {REPORT_REASONS.map(r => (
                                    <label
                                        key={r.value}
                                        className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-body-1 hover:bg-surface-subtle"
                                    >
                                        <input
                                            type="radio"
                                            name={`${uid}-reason`}
                                            value={r.value}
                                            checked={reason === r.value}
                                            onChange={() => setReason(r.value)}
                                            className="size-4 accent-primary"
                                        />
                                        {r.label}
                                    </label>
                                ))}
                            </fieldset>
                            <label
                                htmlFor={`${uid}-details`}
                                className="text-body-2 text-ink"
                            >
                                Anything else? (optional)
                            </label>
                            <textarea
                                id={`${uid}-details`}
                                value={details}
                                maxLength={MAX_REPORT_DETAILS}
                                rows={3}
                                onChange={e => setDetails(e.target.value)}
                                className="w-full resize-none rounded-md border border-border-strong bg-surface px-3 py-2 text-body-2 text-ink outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            />
                            {error && (
                                <p
                                    role="alert"
                                    className="text-body-2 text-error"
                                >
                                    {error}
                                </p>
                            )}
                        </DialogBody>
                        <DialogFooter>
                            <Button
                                type="button"
                                size="sm"
                                variant="naked"
                                onClick={close}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                size="sm"
                                disabled={!reason || state === 'busy'}
                            >
                                Send report
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
};

/** The ⋯ menu: Delete for your own, Report and Block for everyone else's. */
const ItemMenu = ({
    label,
    mine,
    canDelete,
    author,
    onDelete,
    onReport,
    onBlock,
    small,
}: {
    label: string;
    mine: boolean;
    canDelete: boolean;
    /** Null for a restaurant (Report only: restaurants aren't blocked). */
    author: FeedAuthor | null;
    onDelete: () => void;
    onReport: () => void;
    onBlock: () => void;
    small?: boolean;
}) => (
    <DropdownMenu modal={false}>
        <DropdownMenuTrigger
            className={
                small
                    ? `${roundButton} size-7`
                    : `${roundButton} bg-surface-subtle`
            }
        >
            <DotsIcon />
            <span className="sr-only">{label}</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="tw min-w-52">
            {canDelete && (
                <DropdownMenuItem
                    onSelect={onDelete}
                    className="gap-3 text-error"
                >
                    <TrashIcon />
                    Delete
                </DropdownMenuItem>
            )}
            {!mine && (
                <>
                    <DropdownMenuItem onSelect={onReport} className="gap-3">
                        <FlagIcon />
                        Report
                    </DropdownMenuItem>
                    {author && (
                        <DropdownMenuItem onSelect={onBlock} className="gap-3">
                            <BlockIcon />
                            Block {author && authorName(author)}
                        </DropdownMenuItem>
                    )}
                </>
            )}
        </DropdownMenuContent>
    </DropdownMenu>
);

// Lists (prototype SaveToListModal): only with ENABLE_LISTS (the feed already needs accounts).
const SaveButton:
    typeof import('../../components/lists/SaveButton').SaveButton | null =
    process.env.ENABLE_LISTS
        ? // eslint-disable-next-line @typescript-eslint/no-require-imports
          require('../../components/lists/SaveButton').SaveButton
        : null;

/** The dish or restaurant a list_save post names, linking to its page, with Save. */
const SavedItem = ({ post, saved }: { post: FeedPost; saved?: boolean }) => {
    const item = post.item;
    const name = item?.name ?? 'No longer on NearBites';
    const image = item?.image?.templatedUrl ?? null;
    const body = (
        <>
            <span className="size-20 shrink-0 overflow-hidden rounded-md bg-surface-muted md:size-24">
                {image ? (
                    <TemplatedLazyImage
                        templatedUrl={image}
                        size="192x192"
                        options="fit=cover"
                        alt=""
                        className="block size-full object-cover"
                    />
                ) : (
                    <img
                        src={placeholderSrc}
                        alt=""
                        className="block size-full object-cover"
                    />
                )}
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-subtitle-1 font-semibold break-words text-ink-strong">
                    {name}
                </span>
                {item?.restaurantName && (
                    <span className="text-overline tracking-wide text-ink-muted uppercase">
                        {item.restaurantName}
                    </span>
                )}
                {item?.price && (
                    <span className="text-body-2 text-ink">
                        {item.price.display}
                    </span>
                )}
            </span>
        </>
    );
    const inner = 'flex min-w-0 flex-1 items-center gap-4';
    return (
        <div className="mt-3 flex items-center gap-3 rounded-lg bg-surface-subtle p-3">
            {item?.path ? (
                <Link
                    to={item.path}
                    className={`${inner} rounded-md no-underline outline-none focus-visible:ring-2 focus-visible:ring-ring`}
                >
                    {body}
                </Link>
            ) : (
                <div className={inner}>{body}</div>
            )}
            {SaveButton && item?.name && (
                <SaveButton
                    legacyMealId={item.legacyMealId}
                    legacyRestaurantId={item.legacyRestaurantId}
                    itemName={item.name}
                    initiallySaved={saved}
                    className="shrink-0"
                />
            )}
        </div>
    );
};

const Reactions = ({
    post,
    onToggle,
}: {
    post: FeedPost;
    onToggle: (emoji: string) => void;
}) => {
    const labelOf = (emoji: string) =>
        REACTIONS.find(r => r.emoji === emoji)?.label ?? emoji;
    const unused = REACTIONS.filter(
        r => !post.reactions.some(x => x.emoji === r.emoji),
    );
    return (
        <>
            {post.reactions.map(r => (
                <button
                    key={r.emoji}
                    type="button"
                    aria-pressed={r.reacted}
                    onClick={() => onToggle(r.emoji)}
                    className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-body-2 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring ${r.reacted ? 'border-primary bg-primary/10 text-primary' : 'border-transparent bg-surface-subtle text-ink hover:bg-surface-muted'}`}
                >
                    <span aria-hidden="true">{r.emoji}</span>
                    {r.count}
                    <span className="sr-only">{labelOf(r.emoji)}</span>
                </button>
            ))}
            {unused.length > 0 && (
                <DropdownMenu modal={false}>
                    <DropdownMenuTrigger
                        className={`${roundButton} bg-surface-subtle`}
                    >
                        <SmileIcon />
                        <span className="sr-only">Add a reaction</span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="start"
                        className="tw flex min-w-0 gap-1 p-1"
                    >
                        {unused.map(r => (
                            <DropdownMenuItem
                                key={r.emoji}
                                onSelect={() => onToggle(r.emoji)}
                                className="justify-center px-2 text-title-3"
                            >
                                <span aria-hidden="true">{r.emoji}</span>
                                <span className="sr-only">{r.label}</span>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </>
    );
};

const Comments = ({
    post,
    viewer,
    onCount,
    onReport,
    onBlock,
}: {
    post: FeedPost;
    viewer: FeedAuthor;
    onCount: (delta: number) => void;
    onReport: (id: string) => void;
    onBlock: (author: FeedAuthor) => void;
}) => {
    const uid = useId();
    const [comments, setComments] = useState<FeedComment[] | null>(null);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    React.useEffect(() => {
        let cancelled = false;
        v2Session<{ postComments: FeedComment[] }>(POST_COMMENTS, {
            postId: post.id,
        })
            .then(d => !cancelled && setComments(d.postComments))
            .catch(err => {
                if (cancelled) return;
                setComments([]);
                setError(failure(err, "We couldn't load the comments."));
            });
        return () => {
            cancelled = true;
        };
    }, [post.id]);

    const add = async (e: React.FormEvent) => {
        e.preventDefault();
        const content = draft.trim();
        if (!content) return;
        setBusy(true);
        setError('');
        try {
            const { addComment } = await v2Session<{ addComment: FeedComment }>(
                ADD_COMMENT,
                { postId: post.id, content },
            );
            setComments(prev => [...(prev ?? []), addComment]);
            setDraft('');
            onCount(1);
        } catch (err) {
            setError(failure(err, "We couldn't post your comment. Try again."));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (comment: FeedComment) => {
        if (!window.confirm('Delete this comment?')) return;
        try {
            await v2Session(DELETE_COMMENT, { id: comment.id });
            setComments(prev => (prev ?? []).filter(c => c.id !== comment.id));
            onCount(-1);
        } catch (err) {
            setError(failure(err, "We couldn't delete that comment."));
        }
    };

    return (
        <div className="mt-4 flex flex-col gap-3 border-t border-border-subtle pt-4">
            {comments === null && (
                <p className="text-body-2 text-ink-muted">Loading comments…</p>
            )}
            {comments?.map(c => (
                <div key={c.id} className="flex items-start gap-3">
                    <Avatar author={c.author} size="sm" />
                    <div className="min-w-0 flex-1 rounded-lg bg-surface-subtle px-4 py-2">
                        <div className="flex items-baseline justify-between gap-2">
                            <span className="text-body-2">
                                <AuthorLink author={c.author} />
                            </span>
                            <span className="flex shrink-0 items-center gap-1 text-caption text-ink-muted">
                                {timeAgo(c.createdAt)}
                                <ItemMenu
                                    small
                                    label="Comment options"
                                    mine={c.mine}
                                    canDelete={c.mine || post.mine}
                                    author={c.author}
                                    onDelete={() => remove(c)}
                                    onReport={() => onReport(c.id)}
                                    onBlock={() => onBlock(c.author)}
                                />
                            </span>
                        </div>
                        <p className="text-body-2 break-words whitespace-pre-line text-ink">
                            {c.content}
                        </p>
                    </div>
                </div>
            ))}
            <form onSubmit={add} className="flex items-center gap-3">
                <Avatar author={viewer} size="sm" />
                <div className="flex min-w-0 flex-1 items-center rounded-full bg-surface-subtle py-1 pr-1 pl-4 focus-within:ring-2 focus-within:ring-ring">
                    <label htmlFor={`${uid}-comment`} className="sr-only">
                        Add a comment
                    </label>
                    <input
                        id={`${uid}-comment`}
                        value={draft}
                        maxLength={MAX_COMMENT}
                        placeholder="Add a comment…"
                        onChange={e => setDraft(e.target.value)}
                        className="min-w-0 flex-1 bg-transparent py-1.5 text-body-2 text-ink outline-none placeholder:text-ink-muted"
                    />
                    <button
                        type="submit"
                        disabled={busy || !draft.trim()}
                        className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground outline-none hover:bg-primary-hover focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                    >
                        <SendIcon />
                        <span className="sr-only">Send comment</span>
                    </button>
                </div>
            </form>
            {error && (
                <p role="alert" className="text-body-2 text-error">
                    {error}
                </p>
            )}
        </div>
    );
};

/** A restaurant's photo, or its initials, in the avatar circle. */
export const RestaurantAvatar = ({
    restaurant,
    size = 'md',
}: {
    restaurant: FeedRestaurant;
    size?: 'sm' | 'md';
}) => (
    <span
        className={`flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-border-subtle bg-surface-subtle font-medium text-ink-muted ${size === 'sm' ? 'size-9 text-caption' : 'size-11 text-body-2'}`}
    >
        {restaurant.image ? (
            <img
                src={sized(restaurant.image, '96x96-fit=cover')}
                alt=""
                className="size-full object-cover"
            />
        ) : (
            initials(restaurant.name ?? '?')
        )}
    </span>
);

/** The mock's restaurant post header: photo, name, "Restaurant" badge, age, then suburb. */
const RestaurantHeader = ({
    post,
    restaurant,
}: {
    post: FeedPost;
    restaurant: FeedRestaurant;
}) => (
    <>
        <RestaurantAvatar restaurant={restaurant} />
        <div className="min-w-0 flex-1 self-center">
            <p className="flex flex-wrap items-center gap-x-2 text-body-1 text-ink-muted">
                {restaurant.path ? (
                    <Link
                        to={restaurant.path}
                        className="font-semibold text-ink-strong no-underline outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
                    >
                        {restaurant.name ?? 'A restaurant'}
                    </Link>
                ) : (
                    <span className="font-semibold text-ink-strong">
                        {restaurant.name ?? 'A restaurant'}
                    </span>
                )}
                <span className="rounded-full bg-surface-subtle px-2 py-0.5 text-caption text-ink-muted">
                    Restaurant
                </span>
                <span className="text-body-2">
                    ·{' '}
                    <time dateTime={post.createdAt}>
                        {timeAgo(post.createdAt)}
                    </time>
                </span>
            </p>
            {restaurant.locality && (
                <p className="text-body-2 text-ink-muted">
                    {restaurant.locality}
                </p>
            )}
        </div>
    </>
);

/** One photo full width; two to four in a grid. */
export const PostImages = ({ images }: { images: FeedImage[] }) =>
    images.length === 0 ? null : images.length === 1 ? (
        <img
            src={sized(images[0], '1280x800-fit=cover')}
            alt=""
            loading="lazy"
            className="mt-3 aspect-[16/9] w-full rounded-lg object-cover"
        />
    ) : (
        <div className="mt-3 grid grid-cols-2 gap-2">
            {images.map(img => (
                <img
                    key={img.templatedUrl}
                    src={sized(img, '640x640-fit=cover')}
                    alt=""
                    loading="lazy"
                    className="aspect-square w-full rounded-lg object-cover"
                />
            ))}
        </div>
    );

/** The linked dish (mock: thumbnail, name, price, chevron). */
export const DishRow = ({ item }: { item: FeedItem }) => {
    const body = (
        <>
            <span className="size-14 shrink-0 overflow-hidden rounded-md bg-surface-muted">
                <img
                    src={
                        item.image
                            ? sized(item.image, '112x112-fit=cover')
                            : placeholderSrc
                    }
                    alt=""
                    className="block size-full object-cover"
                />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-subtitle-1 font-semibold text-ink-strong">
                    {item.name}
                </span>
                {item.price && (
                    <span className="text-body-2 text-ink-muted">
                        {item.price.display}
                    </span>
                )}
            </span>
            {item.path && (
                <span className="text-ink-muted">
                    <ChevronIcon />
                </span>
            )}
        </>
    );
    const box = 'mt-3 flex items-center gap-4 rounded-lg bg-surface-subtle p-3';
    return item.path ? (
        <Link
            to={item.path}
            className={`${box} no-underline outline-none hover:bg-surface-muted focus-visible:ring-2 focus-visible:ring-ring`}
        >
            {body}
        </Link>
    ) : (
        <div className={box}>{body}</div>
    );
};

export const PostCard = ({
    post,
    viewer,
    onChange,
    onRemoved,
    onBlocked,
    saved,
}: {
    post: FeedPost;
    viewer: FeedAuthor;
    /** Whether the saved item is on one of the viewer's lists (asked for the whole page at once). */
    saved?: boolean;
    onChange: (post: FeedPost) => void;
    onRemoved: () => void;
    onBlocked: (username: string) => void;
}) => {
    const [showComments, setShowComments] = useState(false);
    const [report, setReport] = useState<{
        kind: 'post' | 'comment';
        id: string;
    } | null>(null);
    const [error, setError] = useState('');

    const react = async (emoji: string) => {
        const before = post;
        const was =
            post.reactions.find(r => r.emoji === emoji)?.reacted ?? false;
        onChange({ ...post, reactions: toggleReaction(post.reactions, emoji) });
        try {
            await v2Session(was ? REMOVE_REACTION : ADD_REACTION, {
                postId: post.id,
                emoji,
            });
        } catch (err) {
            onChange(before);
            setError(failure(err, "We couldn't save your reaction."));
        }
    };

    const remove = async () => {
        if (!window.confirm('Delete this post?')) return;
        try {
            await v2Session(DELETE_POST, { id: post.id });
            onRemoved();
        } catch (err) {
            setError(failure(err, "We couldn't delete this post."));
        }
    };

    const block = async (author: FeedAuthor) => {
        const name = authorName(author);
        if (
            !window.confirm(
                `Block ${name}? You won't see each other's posts or comments, and you'll stop following each other.`,
            )
        )
            return;
        try {
            await v2Session(BLOCK_USER, { username: author.username });
            onBlocked(author.username);
        } catch (err) {
            setError(failure(err, `We couldn't block ${name}.`));
        }
    };

    const listLink = post.list && post.author && (
        <Link
            to={`/user/${encodeURIComponent(post.author.username)}/list/${encodeURIComponent(post.list.slug)}`}
            className="font-semibold text-ink-strong no-underline hover:text-primary"
        >
            {post.list.name}
        </Link>
    );

    return (
        <article className="border-b border-border-subtle py-6">
            <header className="flex items-start gap-3">
                {post.restaurant ? (
                    <RestaurantHeader
                        post={post}
                        restaurant={post.restaurant}
                    />
                ) : (
                    post.author && (
                        <>
                            <Avatar author={post.author} />
                            <p className="min-w-0 flex-1 self-center text-body-1 text-ink-muted">
                                <AuthorLink author={post.author} />
                                {post.type === 'LIST_SAVE' && (
                                    <>
                                        {' '}
                                        saved{' '}
                                        {post.item?.legacyMealId
                                            ? 'a dish'
                                            : 'a restaurant'}{' '}
                                        to {listLink}
                                    </>
                                )}
                                <span className="text-body-2">
                                    {' · '}
                                    <time dateTime={post.createdAt}>
                                        {timeAgo(post.createdAt)}
                                    </time>
                                </span>
                            </p>
                        </>
                    )
                )}
                <ItemMenu
                    label="Post options"
                    mine={post.mine}
                    canDelete={post.mine}
                    author={post.author}
                    onDelete={remove}
                    onReport={() => setReport({ kind: 'post', id: post.id })}
                    onBlock={() => post.author && block(post.author)}
                />
            </header>

            {post.type === 'LIST_SAVE' ? (
                <SavedItem post={post} saved={saved} />
            ) : (
                post.content && (
                    <p className="mt-3 text-body-1 break-words whitespace-pre-line text-ink">
                        {post.content}
                    </p>
                )
            )}
            {post.type === 'RESTAURANT' && (
                <>
                    <PostImages images={post.images} />
                    {post.item?.name && <DishRow item={post.item} />}
                </>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
                <Reactions post={post} onToggle={react} />
                <button
                    type="button"
                    aria-expanded={showComments}
                    onClick={() => setShowComments(s => !s)}
                    className="ml-1 rounded-sm px-1 text-body-2 text-ink-muted outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
                >
                    {post.commentCount > 0
                        ? commentCountText(post.commentCount)
                        : 'Comment'}
                </button>
            </div>

            {error && (
                <p role="alert" className="mt-2 text-body-2 text-error">
                    {error}
                </p>
            )}

            {showComments && (
                <Comments
                    post={post}
                    viewer={viewer}
                    onCount={delta =>
                        onChange({
                            ...post,
                            commentCount: Math.max(
                                0,
                                post.commentCount + delta,
                            ),
                        })
                    }
                    onReport={id => setReport({ kind: 'comment', id })}
                    onBlock={block}
                />
            )}

            <ReportDialog target={report} onClose={() => setReport(null)} />
        </article>
    );
};
